// src/App.js
import React, { useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import './styles/responsive.css';
import Login from './components/Login';
import ForgetPassword from './components/ForgetPassword';
import AdminDashboard from './components/AdminDashboard';
import UserDashboard from './components/user/UserDashboard';
import UserProfile from './components/user/UserProfile';
import ComplaintForm from './components/user/ComplaintForm';
import Layout from './components/Layout';
import AllComplaints from './components/user/Allcomplaints';
import ResolvedComplaints from './components/user/ResolvedComplaints';
import PendingComplaints from './components/user/PendingComplaints';
import LegislativeAssemblyModule from './components/user/LegislativeAssemblyModule';
import DistrictModule from './components/user/DistrictModule';
import Users from './components/Users';
import Category from './components/Category';
import ComplaintsList from './components/user/ComplaintsList';
import ComplaintDetails from './components/user/ComplaintDetails';
import ComplaintEdit from './components/user/ComplaintEdit';
import OcrUploader from './components/OcrUploader';
import Settings from './components/user/Settings';
import BackupRestore from './components/BackupRestore';
import SmsManagement from './components/SmsManagement';
import PrivateRoute from './components/PrivateRoute';
import AssignedComplaints from './components/user/AssignedComplaints';
import AuditLogViewer from './components/admin/AuditLogViewer';
import AnalyticsPage from './components/analytics/AnalyticsPage';
import ReportsPage from './components/analytics/ReportsPage';

function App() {
  // Initialize authentication state from localStorage
  const [auth, setAuth] = useState({
    token: localStorage.getItem('token'),
    role: localStorage.getItem('role'),
    username: localStorage.getItem('username'),
  });

  // Initialize theme state from localStorage
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark';
  });

  const isAuthenticated = !!auth.token;
  const role = auth.role;

  // Function to update authentication state & persist to localStorage
  const updateAuth = (token, role, username) => {
    localStorage.setItem('token', token);
    localStorage.setItem('role', role);
    localStorage.setItem('username', username);
    setAuth({ token, role, username });
  };

  // Create theme based on dark mode state
  const theme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: {
        main: '#1976d2',
      },
      secondary: {
        main: '#dc004e',
      },
      background: {
        default: isDarkMode ? '#1a1a1a' : '#f8f9fa',
        paper: isDarkMode ? '#2d2d2d' : '#ffffff',
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login updateAuth={updateAuth} />} />
        <Route path="/forget-password" element={<ForgetPassword />} />

        {/* Protected Routes */}
        <Route element={<PrivateRoute isAuthenticated={isAuthenticated} />}>
          {/* Shared Layout with Navbar, etc. */}
          <Route element={<Layout />}>
            {/* You can include an index route */}
            <Route index element={role === 'admin' ? <UserDashboard /> : <UserDashboard />} />
            <Route path="all-complaints" element={<AllComplaints />} />
            <Route path="pending-complaints" element={<PendingComplaints />} />
            <Route path="complaint_new" element={<ComplaintForm />} />
            <Route path="resolved-complaints" element={<ResolvedComplaints />} />
            <Route path="complaints" element={<ComplaintsList />} />
            <Route path="users" element={<Users />} />
            <Route path="assemblies" element={<LegislativeAssemblyModule />} />
            <Route path="districts" element={<DistrictModule />} />
            <Route path="category" element={<Category />} />
            <Route path="complaint/:id" element={<ComplaintDetails />} />
            <Route path="complaints/edit/:id" element={<ComplaintEdit />} />
            <Route path="ocr" element={<OcrUploader />} />
            <Route path="settings" element={<Settings />} />
            <Route path="profile" element={<UserProfile />} />
            <Route path="backup-restore" element={role === 'admin' ? <BackupRestore /> : <Navigate to="/login" replace />} />
            <Route path="sms-management" element={role === 'admin' ? <SmsManagement /> : <Navigate to="/login" replace />} />
            <Route path="admin" element={role === 'admin' ? <UserDashboard /> : <UserDashboard />} />
            <Route path="my-assigned" element={<AssignedComplaints />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="audit-logs" element={role === 'admin' ? <AuditLogViewer /> : <Navigate to="/login" replace />} />
            <Route path="reports" element={role === 'admin' ? <ReportsPage /> : <Navigate to="/login" replace />} />
          </Route>
        </Route>

        {/* Catch-all: redirect unknown routes to login */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </ThemeProvider>
  );
}

export default App;
